// src/modules/app-views/goodsReceipt/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import GoodsReceiptInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import goodsReceiptInitActionButton from './pages/Init/actions/actionButton';
import goodsReceiptInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import GoodsReceiptDocument from './pages/Document';
import goodsReceiptDocumentActionButton from './pages/Document/actions/actionButton';
import goodsReceiptDocumentMenuButton from './pages/Document/actions/menuButton';
import goodsReceiptDocumentDecorateRequest from './pages/Document/decorators/request';
import CardHeaderGoodsReceiptTitle from './components/CardHeaderGoodsReceiptTitle';
import CardHeaderGoodsReceiptSubtitle from './components/CardHeaderGoodsReceiptSubtitle';
import CardHeaderGoodsReceiptNumberDateTotal from './components/CardHeaderGoodsReceiptNumberDateTotal';
import CardHeaderGoodsReceiptOperationItems from './components/CardHeaderGoodsReceiptOperationItems';
import CardHeaderGoodsReceiptIssuerInfo from './components/CardHeaderGoodsReceiptIssuerInfo';
import CardHeaderGoodsReceiptTags from './components/CardHeaderGoodsReceiptTags';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import seedGoodsReceiptParameters from './seed';
import GoodsReceiptParametersModal from './pages/Init/modals/GoodsReceiptParametersModal';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'goodsReceipt';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: GoodsReceiptParametersModal,
  parametersSeed: seedGoodsReceiptParameters,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <GoodsReceiptInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.GOODS_RECEIPTS.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox', dateField: 'createdAt', dateRange: null },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'createdAt', 'operationNature'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      filterByCompany: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', goodsReceiptInitActionButton, goodsReceiptInitMenuButton],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['purchases', 'goods_receipt'],
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <GoodsReceiptDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.GOODS_RECEIPTS.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: goodsReceiptDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'draft',
        origin: 'manual',
        documentType: 'nfe',
        operationType: 'national',
        entryDate: new Date(),
      },
      actions: [
        'editRefresh',
        goodsReceiptDocumentActionButton(),
        goodsReceiptDocumentMenuButton(),
        'return',
      ],
      viewModeOnOpen: 'view',
      viewModeAfterSave: 'edit',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
        placeholder: 'document',
        noShowInitials: true,
        noShowColor: true,
        hideToolbar: true,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['purchases', 'goods_receipt'],
      },

      card: {
        title: () => <CardHeaderGoodsReceiptTitle />,
        subtitle: () => <CardHeaderGoodsReceiptSubtitle />,

        column1: props => <CardHeaderGoodsReceiptNumberDateTotal {...props} forceShow={true} />,
        column2: props => <CardHeaderGoodsReceiptOperationItems {...props} forceShow={true} />,
        column3: props => <CardHeaderGoodsReceiptIssuerInfo {...props} forceShow={true} />,

        icons: data => <CardHeaderIcons data={data} />,
        tags: () => <CardHeaderGoodsReceiptTags />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {},
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
